import * as React from 'react';
import { connect } from 'react-redux';

import VerticalSpacing from 'components/vertical-spacing/VerticalSpacing';

import InspirationalMaterialsDiscoveryFilters from './filters/InspirationalMaterialsDiscoveryFilters';
import InspirationalMaterialsGrid from '../inspirational-materials-grid/InspirationalMaterialsGrid';

import { getPredefinedHashtag } from 'modules/discovery/content/utils/content';
import { buildImage } from 'utils/image';

import {
  getContent,
  resetContent,
  resetFilters,
  updateContentFilters,
  updateScreenState
} from 'modules/discovery/content/reducers/content';
import { addInstagramImage } from '../../reducers/inspirationalMaterials';

import { __t, applyTranslations } from 'utils/language';

import type {
  DiscoveryContentFilters as DiscoveryContentFiltersType,
  DiscoveryContentLoading,
  DiscoveryContentResult,
  DiscoveryFilterOptions,
  DiscoveryScreenState
} from 'types';
import type { ConsentReducerConsentsMap } from 'redux-store/types/ConsentReducer.type';

import './inspirational-materials-discovery.scss';

applyTranslations(require('./inspirational-materials-discovery.i18n.json'));

type StateProps = {
  results: Array<DiscoveryContentResult>,
  searchTerms: Array<string>,
  screenState: DiscoveryScreenState,
  isLoading: DiscoveryContentLoading,
  userChannelConsents: ConsentReducerConsentsMap
};

type DispatchProps = {
  dispatchFetchContent: (options?: DiscoveryFilterOptions) => Promise<void>,
  dispatchUpdateFilters: (a: Partial<DiscoveryContentFiltersType>) => Promise<void>,
  dispatchUpdateScreenState: (a: DiscoveryScreenState) => Promise<void>,
  dispatchResetContent: () => void,
  dispatchResetFilters: (a: boolean) => void,
  dispatchAddInstagramImage: (a: string) => void
};

type Props = StateProps & DispatchProps;

const InspirationalMaterialsDiscovery = (
  {
    results,
    isLoading,
    searchTerms,
    screenState,
    userChannelConsents,
    dispatchFetchContent,
    dispatchUpdateFilters,
    dispatchResetFilters,
    dispatchResetContent,
    dispatchUpdateScreenState,
    dispatchAddInstagramImage
  }: Props
): JSX.Element => {
  const triggerSearch = (page: number = 1) => {
    if (page === 1) {
      dispatchResetContent();
    }

    dispatchFetchContent({
      itemsPerPage: 35,
      page
    });
  };

  React.useEffect(() => {
    dispatchUpdateFilters({ searchTerms: getPredefinedHashtag() });
    triggerSearch();

    return () => {
      dispatchResetFilters(true);
      dispatchResetContent();
    };
  }, []);

  const hasMorePages = (screenState.meta?.pagesTotal ?? 1) > screenState.page;
  const hasContent = Boolean(isLoading.results.more || results.length > 0);

  return (
    <section className="inspirational-materials-discovery">
      <section className="inspirational-materials-discovery__filters">
        <InspirationalMaterialsDiscoveryFilters
          sort={screenState.sort}
          searchTerms={searchTerms}
          onTriggerSearch={() => triggerSearch()}
          onUpdateSort={(sort) => {
            dispatchUpdateScreenState({
              ...screenState,
              sort
            });
            triggerSearch();
          }}
          onUpdateSearchTerms={(terms) => {
            dispatchUpdateFilters({ searchTerms: terms });
            triggerSearch();
          }}
        />
      </section>
      {
        !hasContent && (
          <section className="inspirational-materials-discovery__empty">
            {__t('inspirational_materials_discovery.empty_message')}
          </section>
        )
      }
      {
        hasContent && (
          <section className="inspirational-materials-discovery__grid">
            <InspirationalMaterialsGrid
              userChannelConsents={userChannelConsents}
              assets={results.map(image => buildImage(image))}
              onSelect={(image) => {
                dispatchAddInstagramImage(image.assetUrl.replace('size=m', 'size=l'));
              }}
              isLoading={isLoading.results.more}
              hasMorePages={hasMorePages}
              onLoadMore={() => triggerSearch(screenState.page + 1)}
            />
            <VerticalSpacing type="small" />
          </section>
        )
      }
    </section>
  );
};

const mapStateToProps = ({ discoveryContent, consentReducer }): StateProps => ({
  results: discoveryContent.results,
  searchTerms: discoveryContent.filters.searchTerms,
  screenState: discoveryContent.screenState,
  isLoading: discoveryContent.isLoading,
  userChannelConsents: consentReducer.consents
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchContent: (options?: DiscoveryFilterOptions): Promise<void> => dispatch(getContent(options)),
  dispatchUpdateFilters: (filters: Partial<DiscoveryContentFiltersType>): Promise<void> => dispatch(updateContentFilters(filters)),
  dispatchUpdateScreenState: (screenState: DiscoveryScreenState): Promise<void> => dispatch(updateScreenState(screenState)),

  dispatchResetContent: () =>
    dispatch(resetContent()),

  dispatchResetFilters: (clearSearchTerms: boolean) =>
    dispatch(resetFilters(clearSearchTerms)),

  dispatchAddInstagramImage: (postImageUrl: string) =>
    dispatch(addInstagramImage(postImageUrl))
});

export default connect(mapStateToProps, mapDispatchToProps)(InspirationalMaterialsDiscovery);
